import { IUndoRedoService } from '../../undo/common/undo.js';
import { ILogService } from '../../log/common/log.js';
import { registerSingleton } from '../../instantiation/common/registry.js';
import { SyncDescriptor } from '../../instantiation/common/descriptors.js';
import { IDisposable } from '../../../base/common/lifecycle.js';
import { Emitter, Event } from '../../../base/common/events.js';
import { IInstantiationService } from '../../instantiation/common/instantiation.js';
import { AICommandResult, CommandServiceDidChangeEvent, CommandsRegistry, ExecuteCommandResult, ICommand, ICommandCanExecuteReturnValue, ICommandContextUser, ICommandContextAI, ICommandEvent, ICommandService, isUndoableCommand } from './commands.js';
import { IAnalyticsService } from '../../analytics/common/analytics.js';



// export const CommandService

/**
 * Default implementation of ICommandService
 */
export class CommandService implements ICommandService {
	static get(): ICommandService {
		if (!this._instance) {
			throw new Error("CommandService instance not initialized yet");
		}
		return this._instance;
	}
	private static _instance: CommandService | null = null;



	constructor(
		@IInstantiationService private readonly instantiationService: IInstantiationService,
		@IUndoRedoService private readonly undoService: IUndoRedoService,
		@ILogService private readonly logger: ILogService,
	) {
		CommandService._instance = this;
	}

	private readonly _onDidChange = new Emitter<CommandServiceDidChangeEvent>();
	readonly onDidChange = this._onDidChange.event;

	private readonly _onWillExecuteCommand = new Emitter<ICommandEvent>();
	readonly onWillExecuteCommand = this._onWillExecuteCommand.event;
	private readonly _onDidExecuteCommand = new Emitter<ICommandEvent>();
	readonly onDidExecuteCommand = this._onDidExecuteCommand.event;


	// Called to execute a typed command with command class inference
	async executeCommand<TCommand extends ICommand<any, any> = ICommand<unknown, unknown>>(
		id: string,
		args: TCommand extends ICommand<infer TArgs, any> ? TArgs : unknown,
		context?: ICommandContextUser
	): Promise<ExecuteCommandResult<TCommand extends ICommand<any, infer TResult> ? TResult : unknown>>;

	// Called to execute a command for AI context
	async executeCommand(id: string, args: unknown, context: ICommandContextAI): Promise<ExecuteCommandResult<AICommandResult>>;

	async executeCommand<TResult = unknown>(id: string, args: unknown, context?: ICommandContextUser | ICommandContextAI): Promise<ExecuteCommandResult<TResult> | ExecuteCommandResult<AICommandResult>> {
		const command = CommandsRegistry.getCommand(id);
		if (!command) {
			this.logger.error(`Command not found: ${id}`);
			throw new Error(`Command '${id}' not found`);
		}

		// Validate if possible
		if (command.canExecute) {
			const res = command.canExecute(args);
			if (res.canExecute === false) {
				this.logger.warn(`Command '${id}' validation failed (reason: ${res.reason || 'unknown'})`);
				return {
					success: false,
					message: `Cannot execute command '${id}': ${res.reason || 'Unknown reason'}`,
					result: null as TResult
				};
			}
		}

		this.logger.debug(`Executing command: ${id}`);

		try {
			// Execute the command
			return this.instantiationService.invokeFunction(async (accessor) => {
				this._onWillExecuteCommand.fire({ commandId: id, args: [args] });

				// Use executeForAI if AI context is requested and available, otherwise fallback to execute
				const res = await
					(context?.isAi && command.executeForAI
						? command.executeForAI(accessor, args, context)
						: command.execute(accessor, args, context)
					);

				this._onDidExecuteCommand.fire({ commandId: id, args: [args] });

				let result: TResult = res as TResult;

				// If invoked for AI, ensure result is a string
				if (context?.isAi) {
					if (typeof res === "string") {
						result = res as TResult;
					}
					else if (typeof res === "number" || typeof res === "boolean") {
						result = String(res) as TResult;
					} else if (res === null || res === undefined) {
						result = `Command '${id}' completed successfully` as TResult;
					} else {
						// For complex objects, use a generic success message
						result = `Command '${id}' completed successfully` as TResult;
					}
				}

				// Auto-push to undo stack if undoable
				if (isUndoableCommand(command)) {
					const action = command.toAction(this.instantiationService, args, context);
					this.undoService.push(action);
				}

				return {
					success: true,
					message: `Command '${id}' executed successfully`,
					result
				};
			});

		} catch (error) {
			this.logger.error(`Error executing command '${id}':`, error);
			throw error;
		}
	}

	canExecuteCommand(id: string, args: unknown): ICommandCanExecuteReturnValue {
		const command = CommandsRegistry.getCommand(id);
		if (!command) {
			return { canExecute: false, reason: `Command '${id}' not found` };
		}

		if (command.canExecute) {
			return command.canExecute(args);
		}

		return {
			canExecute: true
		}
	}
}

registerSingleton(ICommandService, new SyncDescriptor(CommandService));


